import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../i18n/context.js';

interface Props {
  /** CSS selector of the text containers where a selection may be quoted. */
  containerSelector: string;
  onQuote: (text: string) => void;
}

function readSelection(containerSelector: string): { text: string; x: number; y: number } | null {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return null;
  const text = selection.toString().trim();
  if (!text) return null;
  const range = selection.getRangeAt(0);
  const node = range.commonAncestorContainer;
  const el = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
  if (!el?.closest(containerSelector)) return null;
  const rect = range.getBoundingClientRect();
  if (!rect.width && !rect.height) return null;
  return { text, x: rect.left + rect.width / 2, y: rect.top };
}

/** Floating "Reply" button shown above selected message text. */
export function SelectionQuotePopup({ containerSelector, onQuote }: Props) {
  const { t } = useI18n();
  const [state, setState] = useState<{ text: string; x: number; y: number } | null>(null);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setState(readSelection(containerSelector)));
    };
    const hide = () => setState(null);
    document.addEventListener('selectionchange', update);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('selectionchange', update);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [containerSelector]);

  if (!state) return null;
  const left = Math.min(Math.max(state.x, 60), window.innerWidth - 60);
  const top = Math.max(state.y - 8, 48);
  return createPortal(
    <div
      className="selection-quote-popup"
      style={{ left, top }}
      // Keep the selection alive while pressing the button.
      onPointerDown={(event) => event.preventDefault()}
      onMouseDown={(event) => event.preventDefault()}
    >
      <button
        type="button"
        className="selection-quote-btn"
        onClick={() => {
          onQuote(state.text);
          window.getSelection()?.removeAllRanges();
          setState(null);
        }}
      >
        {t('directChat.reply')}
      </button>
    </div>,
    document.body,
  );
}
